/**
 * El estado de una cita, cambiable desde la propia lista.
 *
 * Antes la insignia era sólo una etiqueta: para marcar que alguien asistió había
 * que abrir su ficha, volver, abrir la siguiente. Al terminar el día eso son
 * quince viajes de ida y vuelta para escribir quince veces lo mismo — y es el
 * momento en que menos ganas hay de navegar.
 *
 * Es el mismo mando que el de las ofertas —chip con punta de flecha, menú con lo
 * puesto marcado— y las mismas cuatro acciones que ofrece la ficha, con sus
 * palabras: quien las reconoce en un sitio no aprende nada nuevo en el otro.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { IconAlertTriangle, IconCheck, IconChevronDown, IconLoader2, IconX } from '@tabler/icons-react'

import { BOOKING_STATUS_KEY, BOOKING_STATUS_TONE } from './bookingShared'
import { useI18n } from '../../../lib/i18n'
import type { Booking } from '../../../lib/api'

/** Las mismas de `BookingDetail`, en el mismo orden y con las mismas palabras. */
const CHOICES: { status: Booking['status']; labelKey: string; icon: typeof IconCheck }[] = [
  { status: 'confirmed', labelKey: 'bookings.action.confirm', icon: IconCheck },
  { status: 'completed', labelKey: 'bookings.action.markAttended', icon: IconCheck },
  { status: 'no_show', labelKey: 'bookings.action.noShow', icon: IconAlertTriangle },
  { status: 'cancelled', labelKey: 'bookings.action.cancel', icon: IconX },
]

export function BookingStatusSelect({ booking, busy, onChange, portal = false }: {
  booking: Booking
  busy: boolean
  onChange: (status: Booking['status']) => void
  /** Saca el menú de contenedores con scroll, como la lista de una clase. */
  portal?: boolean | undefined
}) {
  const [open, setOpen] = useState(false)
  const [up, setUp] = useState(false)
  const [portalPosition, setPortalPosition] = useState({ top: 8, left: 8 })
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      setOpen(false)
      wrapRef.current?.querySelector('button')?.focus()
    }
    const onViewportChange = () => { if (portal) setOpen(false) }
    window.addEventListener('keydown', onKey, true)
    window.addEventListener('resize', onViewportChange)
    window.addEventListener('scroll', onViewportChange, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener('resize', onViewportChange)
      window.removeEventListener('scroll', onViewportChange, true)
    }
  }, [open, portal])

  /* El menú grupal vive en un portal para escapar del scroll de la lista. Una
     vez pintado se mide —no se adivina su alto— y se desplaza lo necesario para
     que ni la primera ni la última opción queden fuera de la ventana. */
  useLayoutEffect(() => {
    if (!open || !portal || !menuRef.current) return
    const trigger = wrapRef.current?.querySelector<HTMLButtonElement>(':scope > button')
    if (!trigger) return
    const anchor = trigger.getBoundingClientRect()
    const menu = menuRef.current.getBoundingClientRect()
    const gutter = 8
    const spaceBelow = window.innerHeight - anchor.bottom - gutter
    const spaceAbove = anchor.top - gutter
    const opensUp = spaceBelow < menu.height + 6 && spaceAbove > spaceBelow
    const desiredTop = opensUp ? anchor.top - menu.height - 6 : anchor.bottom + 6
    const maxTop = Math.max(gutter, window.innerHeight - menu.height - gutter)
    const top = Math.max(gutter, Math.min(desiredTop, maxTop))
    const left = Math.max(gutter, Math.min(window.innerWidth - menu.width - gutter, anchor.right - menu.width))
    setPortalPosition((current) => current.top === top && current.left === left ? current : { top, left })
  }, [open, portal])

  const { t } = useI18n()
  const pick = (status: Booking['status']) => {
    setOpen(false)
    if (status !== booking.status) onChange(status)
  }

  const menu = (
    <div
      ref={menuRef}
      className={`dash-status-select-menu${portal ? ' is-portal' : ''}`}
      role="listbox"
      {...(portal ? { style: portalPosition } : {})}
    >
      {CHOICES.map(({ status, labelKey, icon: Icon }) => (
        <button
          key={status}
          type="button"
          role="option"
          aria-selected={booking.status === status}
          className={booking.status === status ? 'is-on' : ''}
          onClick={() => pick(status)}
        >
          <Icon size={14} aria-hidden="true" />
          {t(labelKey)}
          {booking.status === status ? <IconCheck size={14} aria-hidden="true" /> : null}
        </button>
      ))}
    </div>
  )

  return (
    <div
      className={`dash-status-select is-compact${open ? ' is-open' : ''}${up ? ' is-up' : ''}`}
      ref={wrapRef}
      /* La fila de al lado abre la cita al pulsar el nombre; esto vive dentro de
         la misma fila y no tiene por qué contárselo. */
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className={`dash-booking-status is-select ${BOOKING_STATUS_TONE[booking.status]}`}
        disabled={busy}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('bookings.statusOf', { name: booking.customerName || t('bookings.aCustomer') })}
        onClick={(event) => {
          if (!open) {
            const rect = event.currentTarget.getBoundingClientRect()
            const below = window.innerHeight - rect.bottom - 16
            const opensUp = below < 200 && rect.top - 16 > below
            setUp(opensUp)
            if (portal) {
              const menuWidth = 196
              setPortalPosition({
                top: opensUp ? Math.max(8, rect.top - 154) : rect.bottom + 6,
                left: Math.max(8, Math.min(window.innerWidth - menuWidth - 8, rect.right - menuWidth)),
              })
            }
          }
          setOpen((value) => !value)
        }}
      >
        {busy ? <IconLoader2 className="dash-spin" size={12} /> : t(BOOKING_STATUS_KEY[booking.status])}
        <IconChevronDown className="dash-status-select-caret" size={12} aria-hidden="true" />
      </button>

      {open ? (
        portal && typeof document !== 'undefined'
          ? createPortal(<><div className="dash-status-select-backdrop is-portal" role="presentation" onClick={() => setOpen(false)} />{menu}</>, document.body)
          : <><div className="dash-status-select-backdrop" role="presentation" onClick={() => setOpen(false)} />{menu}</>
      ) : null}
    </div>
  )
}
